import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CredentialLogos from './CredentialLogos';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [botField, setBotField] = useState('');
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reject if honeypot was filled (spam)
    if (botField) {
      setSubmitStatus('success');
      return;
    }

    if (!consent) {
      setSubmitError('Please agree to receive emails before subscribing.');
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const payload = new URLSearchParams({
        'form-name': 'newsletter',
        email,
        consent: consent ? 'yes' : ''
      });

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setConsent(false);
      } else {
        throw new Error('Subscription failed');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitError('Something went wrong. Please try again later.');
    }
  };

  return (
    <>
      <CredentialLogos />
      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="newsletter">
              <h3>Stay Updated</h3>
              <p>Subscribe to our newsletter for the latest health tips and updates.</p>
              <form name="newsletter" onSubmit={handleSubmit} data-netlify="true">
                <input type="hidden" name="form-name" value="newsletter" />
                {/* Honeypot */}
                <p style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }} aria-hidden="true">
                  <label>
                    Don't fill this out: <input name="bot-field" value={botField} onChange={e => setBotField(e.target.value)} tabIndex={-1} autoComplete="off" />
                  </label>
                </p>

                {submitStatus === 'success' && (
                  <div className="form-message form-message--success" role="status">
                    Thank you for subscribing!
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="form-message form-message--error" role="alert">
                    {submitError}
                  </div>
                )}

                {submitStatus !== 'success' && (
                  <>
                    <div className="newsletter-fields">
                      <input
                        type="email"
                        name="email"
                        placeholder="Your Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        disabled={submitStatus === 'submitting'}
                      />
                      <button type="submit" disabled={submitStatus === 'submitting'}>
                        {submitStatus === 'submitting' ? 'Subscribing…' : 'Subscribe'}
                      </button>
                    </div>
                    <label className="newsletter-consent">
                      <input
                        type="checkbox"
                        name="consent"
                        checked={consent}
                        onChange={e => setConsent(e.target.checked)}
                        required
                        disabled={submitStatus === 'submitting'}
                      />
                      <span>
                        I agree to receive marketing emails from PH Therapy. See our{' '}
                        <Link to="/privacy">Privacy Policy</Link>.
                      </span>
                    </label>
                  </>
                )}
              </form>
            </div>

            <div className="footer-links">
              <nav className="footer-nav">
                <Link to="/faq">FAQ</Link>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Use</Link>
                <a href="https://www.thehealthnav.co.uk" target="_blank" rel="noopener noreferrer">Find CEPs near you: TheHealthNav</a>
              </nav>
              <p className="footer-disclaimer">
                This website provides general information only. It is not a substitute for professional medical advice. Always consult a healthcare professional before starting any exercise programme.
              </p>
              <div className="footer-bottom">
                <p className="footer-copyright">&copy; 2026 PH Therapy. All rights reserved.</p>

              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
